import { useMemo, useState } from 'react';
import { formatHours } from '@chronos/shared';
import { downloadFile } from '@/api/client';
import { useProjects, useReport, useUsers } from '@/hooks/queries';
import { Card, EmptyState, ErrorNote, PageHeader, Spinner, StatTile } from '@/components/ui';
import { addDays, formatDate, startOfDay, startOfWeek, toDateInput } from '@/lib/format';

type GroupBy = 'user-project' | 'user' | 'project' | 'day';

const PRESETS = [
  { label: 'This week', from: () => startOfWeek(), to: () => addDays(startOfDay(), 1) },
  { label: 'Last week', from: () => addDays(startOfWeek(), -7), to: () => startOfWeek() },
  { label: 'Last 30 days', from: () => addDays(startOfDay(), -30), to: () => addDays(startOfDay(), 1) },
  { label: 'This month', from: () => new Date(new Date().getFullYear(), new Date().getMonth(), 1), to: () => addDays(startOfDay(), 1) },
];

export function ReportsPage() {
  const [fromDate, setFromDate] = useState(() => toDateInput(startOfWeek()));
  const [toDate, setToDate] = useState(() => toDateInput(startOfDay()));
  const [groupBy, setGroupBy] = useState<GroupBy>('user-project');
  const [userIds, setUserIds] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [downloading, setDownloading] = useState<string | null>(null);

  const users = useUsers({ status: 'ACTIVE' });
  const projects = useProjects({ pageSize: 200 });

  // The API treats `to` as exclusive, so send the day after the picked end date.
  const query = useMemo(
    () => ({
      from: new Date(`${fromDate}T00:00:00`).toISOString(),
      to: addDays(new Date(`${toDate}T00:00:00`), 1).toISOString(),
      groupBy,
      userIds: userIds.length ? userIds : undefined,
      projectIds: projectIds.length ? projectIds : undefined,
    }),
    [fromDate, toDate, groupBy, userIds, projectIds],
  );

  const { data, isLoading, error } = useReport(query);

  async function download(format: 'csv' | 'pdf') {
    setDownloading(format);
    try {
      await downloadFile(`/api/reports/export.${format}`, query, `chronos-report-${fromDate}_${toDate}.${format}`);
    } finally {
      setDownloading(null);
    }
  }

  const showDate = groupBy === 'day';

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Reports"
        description="Totals per person and project for any date range."
        actions={
          <>
            <button className="btn-secondary" onClick={() => void download('csv')} disabled={!!downloading}>
              {downloading === 'csv' ? <Spinner className="h-4 w-4" /> : null} Export CSV
            </button>
            <button className="btn-secondary" onClick={() => void download('pdf')} disabled={!!downloading}>
              {downloading === 'pdf' ? <Spinner className="h-4 w-4" /> : null} Export PDF
            </button>
          </>
        }
      />

      <Card className="mb-4 p-4">
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <label className="label" htmlFor="from">From</label>
            <input id="from" type="date" className="input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="to">To</label>
            <input id="to" type="date" className="input" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="group">Group by</label>
            <select id="group" className="input" value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
              <option value="user-project">Person and project</option>
              <option value="user">Person</option>
              <option value="project">Project</option>
              <option value="day">Day and person</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="flex flex-wrap gap-1">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  className="btn-secondary px-2 py-1 text-xs"
                  onClick={() => {
                    setFromDate(toDateInput(p.from()));
                    setToDate(toDateInput(addDays(p.to(), -1)));
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <MultiSelect
            label="People"
            options={(users.data?.items ?? []).map((u) => ({ id: u.id, label: u.name }))}
            selected={userIds}
            onChange={setUserIds}
            emptyLabel="Everyone"
          />
          <MultiSelect
            label="Projects"
            options={(projects.data?.items ?? []).map((p) => ({ id: p.id, label: p.name }))}
            selected={projectIds}
            onChange={setProjectIds}
            emptyLabel="All projects"
          />
        </div>
      </Card>

      <ErrorNote error={error} />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatTile label="Total time" value={formatHours(data?.totalSeconds ?? 0)} tone="brand" />
        <StatTile label="Decimal hours" value={((data?.totalSeconds ?? 0) / 3600).toFixed(2)} />
        <StatTile label="Billable amount" value={data?.totalAmount === null || data?.totalAmount === undefined ? '—' : data.totalAmount.toFixed(2)} hint="from project hourly rates" />
      </div>

      <Card className="mt-4 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : (data?.rows.length ?? 0) === 0 ? (
          <EmptyState title="No time tracked in this range" description="Try widening the dates or clearing the filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  {showDate && <th className="th">Date</th>}
                  <th className="th">Person</th>
                  <th className="th">Project</th>
                  <th className="th text-right">Entries</th>
                  <th className="th text-right">Hours</th>
                  <th className="th text-right">Decimal</th>
                  <th className="th text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.rows.map((r, i) => (
                  <tr key={`${r.userId}-${r.projectId}-${i}`} className="hover:bg-slate-50">
                    {showDate && <td className="td">{formatDate(`${(r as { date?: string }).date}T00:00:00`)}</td>}
                    <td className="td font-medium text-slate-900">{r.userName}</td>
                    <td className="td">{r.projectName}</td>
                    <td className="td text-right tabular-nums text-slate-500">{r.entryCount}</td>
                    <td className="td text-right font-mono tabular-nums">{formatHours(r.seconds)}</td>
                    <td className="td text-right font-mono tabular-nums text-slate-500">{(r.seconds / 3600).toFixed(2)}</td>
                    <td className="td text-right font-mono tabular-nums">{r.amount === null ? '—' : r.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                <tr>
                  <td className="td font-semibold" colSpan={showDate ? 4 : 3}>Total</td>
                  <td className="td text-right font-mono font-semibold tabular-nums">{formatHours(data?.totalSeconds ?? 0)}</td>
                  <td className="td text-right font-mono font-semibold tabular-nums">{((data?.totalSeconds ?? 0) / 3600).toFixed(2)}</td>
                  <td className="td text-right font-mono font-semibold tabular-nums">
                    {data?.totalAmount === null || data?.totalAmount === undefined ? '—' : data.totalAmount.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function MultiSelect({
  label,
  options,
  selected,
  onChange,
  emptyLabel,
}: {
  label: string;
  options: { id: string; label: string }[];
  selected: string[];
  onChange: (v: string[]) => void;
  emptyLabel: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <span className="label">{label}</span>
      <button type="button" className="input flex items-center justify-between text-left" onClick={() => setOpen((v) => !v)}>
        <span className="truncate">{selected.length === 0 ? emptyLabel : `${selected.length} selected`}</span>
        <span className="text-slate-400" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
          {selected.length > 0 && (
            <button type="button" className="w-full rounded px-2 py-1.5 text-left text-xs text-brand-700 hover:bg-slate-50" onClick={() => onChange([])}>
              Clear selection
            </button>
          )}
          {options.map((o) => (
            <label key={o.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={selected.includes(o.id)}
                onChange={(e) => onChange(e.target.checked ? [...selected, o.id] : selected.filter((s) => s !== o.id))}
              />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
